/**
 * Tracking Scripts.
 * This is taken from the Jetsetter and FVC themes.
 * In the future, both of those sites should be converted to use this unified tracking file.
 * As of March 8, 2019 this is only being used by Oyster.
 *
 * @link https://tenup.teamwork.com/#/messages/1082542?scrollTo=3132264&
 *
 * @link https://tenup.teamwork.com/#/tasks/17049384 Implement GTM.
 * @link https://tenup.teamwork.com/#/tasks/17049391 Source Tracking.
 * @link https://tenup.teamwork.com/#files/5021965   Jetsetter Tracking.
 *
 ********************************************************************************
 *
 * This will handle all tracking JS vars.
 * The main vars are sets via wp_localize_scripts() to be available globally,
 * so we can add data via PHP and can be used across the app.
 *
 * - themes\jetsetter\includes\tracking\tracking.php
 *
 * This script/functionality re-implement similar logic in smarter-travel theme:
 *
 * - themes\smarter-travel\assets\js\src\tracking.js
 * - themes\smarter-travel\app\internal-tracking\st-cookies.js
 * - themes\smarter-travel\app\core\utilities\smartertravel-tracking.php
 * - themes\smarter-travel\app\core\utilities\smartertravel-gtm.php
 *
 * Other Notes:
 * For source cookies, there's a related tasks from SmarterTravel JIRA
 * To set source cookies from social trafic.
 * - https://jira.smartertravelmedia.com/browse/TENUP-401
 *
 ********************************************************************************
 */

import 'babel-polyfill';
import 'whatwg-fetch';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import updateHotelsLinks from './update-hotels-links';

const STSharedTracking = window.STSharedTracking || {};
const navigator = window.navigator || {};
window.__stsharedTrackingObj = window.__stsharedTrackingObj || {};

/**
 * Vanilla JS equivalent for Jquery's Jquery $( Document).ready
 * Browser Support IE 9+
 */
function ready(fn) {
	if (document.attachEvent ? document.readyState === 'complete' : document.readyState !== 'loading') {
		fn();
	} else {
		document.addEventListener('DOMContentLoaded', fn);
	}
}

/**
 * Add Tracking Data.
 *
 * The data will be added as object item on "__stsharedTrackingObj".
 * The main use is for Google Tag Manager.
 *
 * @link https://tenup.teamwork.com/#tasks/17049384
 * @link https://docs.google.com/spreadsheets/d/1b_1HsFwdy7Zs0BePJo6F1gLVAo9NIKG-5JoK1aLT_GE/edit?usp=sharing
 *
 * @param {string} key   Object Item Key.
 * @param {string} value Object Item Value.
 * @returns {void}
 */
STSharedTracking.addData = (key = '', value = '') => {
	if ('' === key) {
		return;
	}

	window.__stsharedTrackingObj[key] = value;
};

STSharedTracking.getData = (key = '') => {
	if ('' === key) {
		return;
	}

	return window.__stsharedTrackingObj[key];
};

/**
 * Get Referrer Domain.
 *
 * @returns {string} Referrer Domain.
 */
STSharedTracking.getReferrer = () => {
	const parser = document.createElement('a');

	parser.href = document.referrer;

	return parser.hostname.toLowerCase();
};

/**
 * Get URL var.
 *
 * @link https://css-tricks.com/snippets/javascript/get-url-variables/
 *
 * @param {string} variable URL var.
 * @returns {object} URL Params.
 */
STSharedTracking.getUrlVar = (variable = '', url = '') => {
	let query;
	if (!url) {
		query = window.location.search.substring(1);
	} else {
		query = url.split('?')[1] || '';
	}
	const vars = query.split('&');

	for (let i = 0; i < vars.length; i++) {
		const pair = vars[i].split('=');

		if (pair[0] === variable) {
			return decodeURIComponent(pair[1]);
		}
	}

	return false;
};

/**
 * Get URL Vars
 *
 * @returns {array} URL vars
 */
STSharedTracking.getURLVars = () => {
	const vars = {};
	const queryString = window.location.search;
	let hash;

	if (queryString) {
		const hashes = queryString.slice(1).split('&');

		for (let i = 0; i < hashes.length; i++) {
			hash = hashes[i].split('=');
			if ('' !== hash[1]) {
				vars[hash[0]] = hash[1];
			}
		}

		return vars;
	}

	return false;
};

/**
 * Get Cookies.
 * This will only list of supported cookies
 * defined in STSharedTracking.cookies
 *
 * @returns {string} Referrer Domain.
 */
STSharedTracking.getCookies = () => {
	const allCookies = {};
	const trackedCookies = STSharedTracking.campaignCookies;

	trackedCookies.push('uu');
	trackedCookies.push('uu_persist');

	trackedCookies.forEach(function (element) {
		const cookie = STSharedTracking.getCookie(element);

		if (cookie) {
			this[element] = cookie;
		}
	}, allCookies);

	return allCookies;
};

/**
 * @function getConsistentRootDomain
 * Used to share cookies across same domains / subdomains
 *
 * @returns {string} root domain ie. '.airfarewatchdog.com'
 */
function getConsistentRootDomain() {
	if (window.location && window.location.hostname) {
		return location.hostname.split('.').slice(-2).join('.');
	}
	return '';
}

/**
 * Set Cookie.
 *
 * @param {string} name             Cookie name.
 * @param {string} val              Cookie value.
 * @param {object} params           Cookie param.
 * @returns {void}
 */
STSharedTracking.setCookie = (name, val, params = {}) => {
	const domain = getConsistentRootDomain();
	Cookies.set(name, val, { ...params, domain });
};

STSharedTracking.deleteCookie = (name, params = {}) => {
	const domain = getConsistentRootDomain();
	Cookies.remove(name, { ...params, domain });
};

/**
 * Set Cookie No Overwrite.
 * This will set the cookies again,
 * so the exp. time will be updated,
 * but the value still be the same.
 *
 * Use this if we don't want to overwrite the old cookie.
 *
 * @param {string} name             Cookie name.
 * @param {string} val              Cookie value.
 * @param {object} params           Cookie param.
 * @returns {void}
 */
STSharedTracking.setCookieNoOverwrite = (name, val, params = {}) => {
	const exists = Cookies.get(name);
	const domain = getConsistentRootDomain();

	let value = val;

	if (exists) {
		// Use old cookie value.
		value = exists;
	}

	Cookies.set(name, value, { ...params, domain });
};

/**
 * Get Cookie.
 *
 * @param {string} name Cookie name.
 * @returns {void}
 */
STSharedTracking.getCookie = (name) => {
	const domain = getConsistentRootDomain();
	return Cookies.get(name, { domain });
};

/**
 * Delete Cookie.
 *
 * @param {string} name Cookie name.
 * @returns {void}
 */
STSharedTracking.deleteCookie = (name) => {
	Cookies.remove(name, { domain: getConsistentRootDomain() });
};

/**
 * Get a URL or cookie variable.
 *
 * This will look for the variable in the URL querystring first and return it
 * if found, othewise will look for the variable in cookies.
 *
 * @param {string} variable URL var.
 * @returns {object} URL/cookie value, or false.
 */
STSharedTracking.getUrlOrCookieVar = (variable = '') => {
	const urlValue = STSharedTracking.getUrlVar(variable);

	if (false !== urlValue) {
		return urlValue;
	}

	const cookieValue = STSharedTracking.getCookie(variable);

	if (false !== cookieValue) {
		return cookieValue;
	}

	return false;
};

/**
 * Get Campaign Cookies Params
 *
 * @returns {array}
 */
STSharedTracking.campaignCookies = [
	'taparam',
	'sg',
	'source',
	'source_id',
	'af',
	'gclid',
	'msclkid',
	'mcid',
	'nl_cs',
	'nl_id',
	'nl_pid',
	'nl_cid',
	'nl_tv',
	'page_state_id',
	'supai',
	'supap1',
	'supap2',
	'supdev',
	'supmbl',
	'supmt',
	'suppos',
	'supsn',
	'traqparam',
	'u',
];

/**
 * Delete Campaign Cookies
 *
 * @returns {void}
 */
STSharedTracking.deleteCampaignCookies = () => {
	for (const cookies of STSharedTracking.campaignCookies) {
		STSharedTracking.deleteCookie(cookies);
	}
};

/**
 * Generate UUID
 * This is simply generate random string for tracking identifier.
 *
 * This code is from smarter travel tracking js.
 * - st-cookies.js
 *
 * @link https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
 *
 * @returns {string} Referrer Domain.
 */
STSharedTracking.generateUUID = () => {
	return uuidv4();
};

/**
 * Removes a single-letter prefix of the type created in generateUUID, e.g. "p-" or "s-".
 *
 * @param {string} uuid UUID to strip.
 * @returns {string} uuid with prefix stripped.
 */
STSharedTracking.stripUUIDPrefix = (uuid) => {
	if (!uuid) {
		return uuid;
	}

	if (!uuid.match('^.-')) {
		return uuid;
	}

	return uuid.substring(2);
};

/**
 * Is Mobile. Detect mobile user agent.
 * This will also return true on tablet. Use STSharedTracking.isTablet() to check first if needed.
 *
 * @link https://shellcreeper.com/simple-mobile-browser-detection-using-javascript/
 *
 * @returns {bool} True if mobile.
 */
STSharedTracking.isMobile = () => {
	if (
		navigator.userAgent.match(/Mobile/i) ||
		navigator.userAgent.match(/Android/i) ||
		navigator.userAgent.match(/Silk/i) ||
		navigator.userAgent.match(/Kindle/i) ||
		navigator.userAgent.match(/BlackBerry/i) ||
		navigator.userAgent.match(/Opera Mini/i) ||
		navigator.userAgent.match(/Opera Mobi/i)
	) {
		return true;
	}

	return false;
};

/**
 * Is Tablet. Detect mobile user agent.
 *
 * @returns {bool} True if tablet.
 */
STSharedTracking.isTablet = () => {
	if (navigator.userAgent.match(/iPad/i)) {
		return true;
	}

	return false;
};

/**
 * Add Tracking To API Args.
 *
 * @param {object} args Args.
 * @returns {object} Args.
 */
STSharedTracking.addApiArgs = (args = {}) => {
	const tracking = STSharedTracking;
	const StSharedMCIDs = window.StSharedMCIDs || {};

	/* eslint-disable camelcase */

	// Object context.
	if (undefined !== window.__stsharedTrackingObj.pageName) {
		args.t_page_name = window.__stsharedTrackingObj.pageName;
	}
	if (undefined !== window.__stsharedTrackingObj.pageType) {
		args.t_page_type = window.__stsharedTrackingObj.pageType;
	}

	// Mode & MCID default.
	// https://tenup.teamwork.com/#tasks/18506593?c=8438601
	let device = 'web';
	if (tracking.isTablet()) {
		device = 'tablet';
	} else if (tracking.isMobile()) {
		device = 'smartphone';
	}
	args.device = device;

	if (Object.values(StSharedMCIDs).length > 0) {
		let mcid = Object.values(StSharedMCIDs)[0];
		if ('undefined' !== typeof StSharedMCIDs[device]) {
			mcid = StSharedMCIDs[device];
		}

		args.mcid = mcid;
	}

	// MCID.
	if (tracking.getCookie('mcid')) {
		args.mcid = tracking.getCookie('mcid');
	}
	// Source.
	if (tracking.getCookie('source')) {
		args.source_id = tracking.getCookie('source');
	}
	// uu_persist.
	if (tracking.getCookie('uu_persist')) {
		args.t_pbr_publisher_browser_id = tracking.getCookie('uu_persist');
	}
	// u.
	if (tracking.getCookie('u')) {
		args.t_pbr_publisher_user_id = tracking.getCookie('u');
	}
	// page_state_id.
	if (tracking.getCookie('page_state_id')) {
		args.t_pbr_page_state_id = tracking.getCookie('page_state_id');
	}
	// uu.
	if (tracking.getCookie('uu')) {
		args.t_user_session_id = tracking.getCookie('uu');
	}
	// nl_cs.
	if (tracking.getCookie('nl_cs')) {
		const nlcs = tracking.getCookie('nl_cs').split('::');

		if (undefined !== nlcs[0]) {
			args.t_crm_nlid = nlcs[0];
		}
		if (undefined !== nlcs[3]) {
			args.t_crm_testversion = nlcs[3];
		}
	}
	// sem.
	if (tracking.getCookie('gclid')) {
		args.t_sem_gclid = tracking.getCookie('gclid');
	}
	if (tracking.getCookie('supai')) {
		args.t_sem_supai = tracking.getCookie('supai');
	}
	if (tracking.getCookie('supsn')) {
		args.t_sem_supsn = tracking.getCookie('supsn');
	}
	if (tracking.getCookie('supmbl')) {
		args.t_sem_supmbl = tracking.getCookie('supmbl');
	}
	if (tracking.getCookie('suppos')) {
		args.t_sem_suppos = tracking.getCookie('suppos');
	}
	if (tracking.getCookie('supmt')) {
		args.t_sem_supmt = tracking.getCookie('supmt');
	}
	if (tracking.getCookie('supdev')) {
		args.t_sem_supdev = tracking.getCookie('supdev');
	}
	if (tracking.getCookie('supap1')) {
		args.t_sem_supap1 = tracking.getCookie('supap1');
	}
	if (tracking.getCookie('supap2')) {
		args.t_sem_supap2 = tracking.getCookie('supap2');
	}
	if (tracking.getCookie('taparam')) {
		args.t_sem_taparam = tracking.getCookie('taparam');
	}
	// traqparam
	if (tracking.getCookie('traqparam')) {
		args.t_traqparam = tracking.getCookie('traqparam');
	}

	/* eslint-enable camelcase */
	return args;
};

/**
 * Set Date.
 *
 * @param {object} args Args.
 * @returns {void}
 */
STSharedTracking.setDate = () => {
	const cDates = STSharedTracking.getCookie('js_check_in_out_date');

	// Dec 06, 2019
	if (cDates) {
		const [from, to] = cDates.split('to');

		const date1 = dayjs(from.trim(), 'MMM DD, YYYY');
		const date2 = dayjs(to.trim(), 'MMM DD, YYYY');

		if (date1 && date2) {
			STSharedTracking.addData('date1', date1.format('YYYY-MM-DD'));
			STSharedTracking.addData('date2', date2.format('YYYY-MM-DD'));
			STSharedTracking.addData('numNights', date2.diff(date1, 'days'));
		}
	}
};

/**
 * Action: Requests Latitude and Longitude location data from Akamai
 *
 * Sends datetime and UUID to make sure that the results come back uncached
 *
 * Stores itself in the users session to make sure that the API isn't being
 * hit constantly as the user browses around the site.
 *
 * Session data is retrieved and stored in a global `window.__stsharedTrackingObj`
 *
 * @returns {void}
 */
STSharedTracking.getLocationDataFromAkamai = () => {
	const UUID = STSharedTracking.generateUUID();
	const datetime = Date.now();
	const url = 'https://www.smartertravel.com/wp-json/akamai/v1/location';

	if (sessionStorage.getItem('locationData')) {
		// if we already have the session data, we can store it in the global
		STSharedTracking.setGlobalLocationData();
	} else {
		// request location data
		const request = async () => {
			try {
				const response = await fetch(url, {
					// unique header args to make sure results are coming back uncached
					headers: {
						UUID: UUID,
						time: datetime,
					},
				});
				const location = await response.json();

				// store in session so we're not hitting the API unnecessarily
				sessionStorage.setItem('locationData', JSON.stringify(location));

				// store the session location data in a global
				STSharedTracking.setGlobalLocationData();
			} catch (err) {
				console.error(err);
			}
		};
		request();
	}
};

/**
 * Action: retrieve session Latitude and/or Longitude location data
 * and store in Global  `window.__stsharedTrackingObj`
 *
 * @returns {void}
 */
STSharedTracking.setGlobalLocationData = () => {
	const location = JSON.parse(sessionStorage.getItem('locationData'));

	// if we have latitude
	if (location.lat) {
		window.__stsharedTrackingObj.lat = location.lat;
	}

	// if we have longitude
	if (location.long) {
		window.__stsharedTrackingObj.long = location.long;
	}
};

/**
 * Sets the entry URL for the first visit to the site.
 *
 * @type {void}
 */
STSharedTracking.setEntryUrl = () => {
	// Other themes set "entryUrl" to true.
	const cookieName = 'trackingEntryUrl';
	let entryUrl = null;

	// Has the entryUrl already been set?
	const existingEntryUrl = STSharedTracking.getCookie(cookieName);
	if (existingEntryUrl) {
		entryUrl = existingEntryUrl;
	} else {
		// Set the entryUrl to be used for this session.
		const { hostname, pathname } = document.location;
		entryUrl = hostname + pathname;
		STSharedTracking.setCookie(cookieName, entryUrl);
	}

	STSharedTracking.addData('entryUrl', entryUrl);
};

STSharedTracking.initPageStateId = () => {
	if (!window.__stsharedTrackingObj.pageStateId) {
		window.__stsharedTrackingObj.pageStateId = uuidv4();
		updateHotelsLinks(window.__stsharedTrackingObj.pageStateId);
	}
};

/**
 * Action: The main action of setting Cookies.
 *
 * Instead of making functions and group the cookie and process the group (as in SmarterTravel).
 * It's best to simply use as simple code as possible because all this cookie setup will need to be
 * updated in the future with even more complex conditional.
 *
 * @link https://drive.google.com/file/d/1AL4IM2Lk5OCt3XYbjkfTSo2fOqeaxusw/view
 *
 * @returns {void}
 */
STSharedTracking.init = () => {
	/* === Location Data from Akamai ===*/
	STSharedTracking.getLocationDataFromAkamai();

	/* === MOBILE DETECTION === */
	if (STSharedTracking.isTablet()) {
		STSharedTracking.addData('siteMode', 'tablet');
	} else if (STSharedTracking.isMobile()) {
		STSharedTracking.addData('siteMode', 'mobile');
	} else {
		STSharedTracking.addData('siteMode', 'desktop');
	}
	// Is known user?
	if (STSharedTracking.getCookie('uu_persist')) {
		STSharedTracking.addData('isKnownUser', 'true');
	} else {
		STSharedTracking.addData('isKnownUser', 'false');
	}

	// Is logged in?
	ready(() => {
		if (STSharedTracking.getCookie('u')) {
			STSharedTracking.addData('isLoggedIn', 'true');
		} else {
			STSharedTracking.addData('isLoggedIn', 'false');
		}
	});

	/* === ON INITIAL LOAD ADD THIS USER COOKIE === */

	// "uu" Cookie. For current session. No exp. Just for current session.
	const uuUrlVar = STSharedTracking.getUrlVar('uu');
	if (uuUrlVar) {
		STSharedTracking.setCookie('uu', uuUrlVar, {});
	} else {
		STSharedTracking.setCookieNoOverwrite('uu', STSharedTracking.generateUUID(), {});
	}
	STSharedTracking.addData('userSessionId', STSharedTracking.stripUUIDPrefix(STSharedTracking.getCookie('uu')));

	// "page_state_id" Cookie. For current session. No exp. Just for current session.
	if (STSharedTracking.getUrlVar('page_state_id')) {
		STSharedTracking.setCookie('page_state_id', STSharedTracking.getUrlVar('page_state_id'), {});
	} else {
		STSharedTracking.setCookieNoOverwrite('page_state_id', STSharedTracking.generateUUID(), {});
	}

	// "uu_persist" Cookie: for 10 years.
	if (STSharedTracking.getUrlVar('uu_persist')) {
		STSharedTracking.setCookie('uu_persist', STSharedTracking.getUrlVar('uu_persist'), { expires: 365 * 10 });
	}
	STSharedTracking.setCookieNoOverwrite('uu_persist', STSharedTracking.generateUUID(), { expires: 365 * 10 });

	/* === DELETE CAMPAIGN COOKIES === */

	const urlVars = STSharedTracking.getURLVars();
	const campaignCookies = STSharedTracking.campaignCookies;

	for (const campaignCookie of campaignCookies) {
		if (undefined !== urlVars[campaignCookie]) {
			STSharedTracking.deleteCampaignCookies();
			break;
		}
	}

	/* === TAPARAM & SOURCE COOKIE === */
	let needSetSource = true;

	if (STSharedTracking.getUrlVar('taparam')) {
		// Re-add it with new one.
		STSharedTracking.setCookie('taparam', STSharedTracking.getUrlVar('taparam'), { expires: 7 });

		// "sg" cookie: Only used if taparam is specified.
		if (STSharedTracking.getUrlVar('sg')) {
			STSharedTracking.setCookie('sg', STSharedTracking.getUrlVar('sg'), { expires: 7 });
		}

		// Add source cookie based on taparam string.
		let taparamValue = STSharedTracking.getUrlVar('taparam');

		// Get source string.
		// e.g: get "google" from "ESTGoogleUS_K387879838_A20507226982_NS".
		taparamValue = taparamValue.split('EST').pop().split('US_').shift();
		taparamValue = taparamValue.toLowerCase();

		// Set source based on this string.
		let sourceIDtaparam = false;

		if (taparamValue.includes('google')) {
			sourceIDtaparam = '45234';
		} else if (taparamValue.includes('gemini')) {
			sourceIDtaparam = '55882';
		} else if (taparamValue.includes('msn')) {
			sourceIDtaparam = '45235';
		}

		if (sourceIDtaparam) {
			STSharedTracking.setCookie('source', sourceIDtaparam, { expires: 7 });
			needSetSource = false;
		}
	}

	// Set marketing channel to taparam if this query string or cookie variable is present.
	if (STSharedTracking.getUrlOrCookieVar('taparam')) {
		STSharedTracking.addData('marketingChannel', 'taparam');
	}

	if (STSharedTracking.getUrlVar('source')) {
		// If "source" defined add it.
		// Re-add it.
		STSharedTracking.setCookie('source', STSharedTracking.getUrlVar('source'), { expires: 7 });
		needSetSource = false;
	} else if (STSharedTracking.getUrlVar('source_id')) {
		// Deprecated "source_id" param.
		STSharedTracking.setCookie('source', STSharedTracking.getUrlVar('source_id'), { expires: 7 });
		needSetSource = false;
	}

	if (needSetSource) {
		// Use referrer domain as source.
		const refDomain = STSharedTracking.getReferrer();
		let sourceID = false;
		let isSEO = false;
		let isSocial = false;

		if (refDomain.includes('google')) {
			// Search Engine.
			sourceID = '112497';
			isSEO = true;
		} else if (refDomain.includes('yahoo')) {
			sourceID = '112499';
			isSEO = true;
		} else if (refDomain.includes('msn') || refDomain.includes('bing')) {
			sourceID = '112502';
			isSEO = true;
		} else if (refDomain.includes('facebook')) {
			// Social.
			sourceID = '112442';
			isSocial = true;
		} else if (refDomain.includes('pinterest')) {
			sourceID = '112442';
			isSocial = true;
		} else if (refDomain.includes('instagram')) {
			sourceID = '112438';
			isSocial = true;
		} else if (refDomain.includes('twitter') || refDomain.includes('t.co')) {
			sourceID = '112435';
			isSocial = true;
		} else if (refDomain.includes('linkedin')) {
			sourceID = '112433';
			isSocial = true;
		} else if (refDomain.includes('reddit')) {
			sourceID = '112431';
			isSocial = true;
		} else if (
			refDomain.includes('duckduckgo') ||
			refDomain.includes('searchencrypt') ||
			refDomain.includes('gibiru') ||
			refDomain.includes('swisscows') ||
			refDomain.includes('yippy') ||
			refDomain.includes('bitclave') ||
			refDomain.includes('qwant') ||
			refDomain.includes('discretesearch') ||
			refDomain.includes('oscobo') ||
			refDomain.includes('hotbot')
		) {
			sourceID = '121835';
			isSocial = true;
		} else if (
			refDomain.includes('dogpile') ||
			refDomain.includes('excite') ||
			refDomain.includes('ecosia') ||
			refDomain.includes('exactseek') ||
			refDomain.includes('exalead') ||
			refDomain.includes('gigablast') ||
			refDomain.includes('munax') ||
			refDomain.includes('yandex') ||
			refDomain.includes('lycos') ||
			refDomain.includes('teoma') ||
			refDomain.includes('answers') ||
			refDomain.includes('webcrawler') ||
			refDomain.includes('baidu') ||
			refDomain.includes('ask') ||
			refDomain.includes('aol')
		) {
			sourceID = '121839';
			isSocial = true;
		}

		// Set source cookie.
		if (sourceID) {
			STSharedTracking.setCookie('source', sourceID, { expires: 7 });

			if (isSEO) {
				STSharedTracking.addData('marketingChannel', 'seo');
			} else if (isSocial) {
				STSharedTracking.addData('marketingChannel', 'social');
			}

			/**
			 * The following parameter is added to signal to Shared Tracking
			 * files, or in other words, tracking from themes such as AWD
			 * (js/frontend/tracking/tracking.js), that the source param was
			 * set using a referrer, and will likely need to be overridden by
			 * theme specific tracking referrer IDs, since the referrer IDs in
			 * this file seem to be Jetsetter specific. Therefore, the override
			 * will be necessary, and can be made, by checking if this is true.
			 */
			STSharedTracking.sourceWasSetFromSharedReferrer = true;
		}
	}

	const sourceCookieVal = STSharedTracking.getCookie('source');

	if (undefined !== sourceCookieVal) {
		STSharedTracking.addData('marketingSource', sourceCookieVal);
	}

	/* === OTHER COOKIE === */

	// "force_1c1w" cookie.
	if (STSharedTracking.getUrlVar('force_1c1w')) {
		STSharedTracking.setCookie('force_1c1w', true, { expires: 7 });
	}

	// "af" cookie.
	if (STSharedTracking.getUrlVar('af')) {
		STSharedTracking.setCookie('af', STSharedTracking.getUrlVar('af'), { expires: 7 });
	}

	// "gclid" cookie.
	if (STSharedTracking.getUrlVar('gclid')) {
		STSharedTracking.setCookie('gclid', STSharedTracking.getUrlVar('gclid'), { expires: 7 });
	}

	// Note: this was originally a typo as msclid, see TENUP-1326.
	// "msclkid" cookie.
	if (STSharedTracking.getUrlVar('msclkid')) {
		STSharedTracking.setCookie('msclkid', STSharedTracking.getUrlVar('msclkid'), { expires: 7 });
	}

	// "mcid" cookie.
	if (STSharedTracking.getUrlVar('mcid')) {
		STSharedTracking.setCookie('mcid', STSharedTracking.getUrlVar('mcid'), { expires: 7 });
	}

	// "nl_cs" cookie.
	if (STSharedTracking.getUrlVar('nl_cs')) {
		STSharedTracking.setCookie('nl_cs', STSharedTracking.getUrlVar('nl_cs'), { expires: 7 });
	} else if (STSharedTracking.getUrlVar('nl_id') || STSharedTracking.getUrlVar('nl_tv')) {
		const nlid = STSharedTracking.getUrlVar('nl_id') || ''; // NewsLetter ID.
		const nlpid = STSharedTracking.getUrlVar('nl_pid') || ''; // Placement ID.
		const nlcid = STSharedTracking.getUrlVar('nl_cid') || ''; // Content ID.
		const nltv = STSharedTracking.getUrlVar('nl_tv') || ''; // Test Version.

		STSharedTracking.setCookie('nl_cs', `${nlid}::${nlpid}::${nlcid}::${nltv}`, { expires: 7 });
	}

	// "u" UserID cookie.
	if (STSharedTracking.getUrlVar('u')) {
		STSharedTracking.setCookie('u', STSharedTracking.getUrlVar('u'), { expires: 7 });
	}

	// "supai" cookie.
	if (STSharedTracking.getUrlVar('supai')) {
		STSharedTracking.setCookie('supai', STSharedTracking.getUrlVar('supai'), { expires: 7 });
	}

	// "supap1" cookie.
	if (STSharedTracking.getUrlVar('supap1')) {
		STSharedTracking.setCookie('supap1', STSharedTracking.getUrlVar('supap1'), { expires: 7 });
	}

	// "supap2" cookie.
	if (STSharedTracking.getUrlVar('supap2')) {
		STSharedTracking.setCookie('supap2', STSharedTracking.getUrlVar('supap2'), { expires: 7 });
	}

	// "supdev" cookie.
	if (STSharedTracking.getUrlVar('supdev')) {
		STSharedTracking.setCookie('supdev', STSharedTracking.getUrlVar('supdev'), { expires: 7 });
	}

	// "supmbl" cookie.
	if (STSharedTracking.getUrlVar('supmbl')) {
		STSharedTracking.setCookie('supmbl', STSharedTracking.getUrlVar('supmbl'), { expires: 7 });
	}

	// "supmt" cookie.
	if (STSharedTracking.getUrlVar('supmt')) {
		STSharedTracking.setCookie('supmt', STSharedTracking.getUrlVar('supmt'), { expires: 7 });
	}

	// "suppos" cookie.
	if (STSharedTracking.getUrlVar('suppos')) {
		STSharedTracking.setCookie('suppos', STSharedTracking.getUrlVar('suppos'), { expires: 7 });
	}

	// "supsn" cookie.
	if (STSharedTracking.getUrlVar('supsn')) {
		STSharedTracking.setCookie('supsn', STSharedTracking.getUrlVar('supsn'), { expires: 7 });
	}

	// "traqparam" cookie.
	if (STSharedTracking.getUrlVar('traqparam')) {
		STSharedTracking.setCookie('traqparam', STSharedTracking.getUrlVar('traqparam'), { expires: 7 });
	}

	// Set marketing channel to traqparam if this query string or cookie variable is present.
	if (STSharedTracking.getUrlOrCookieVar('traqparam')) {
		STSharedTracking.addData('marketingChannel', 'traq');
	}

	// Set marketing channel to newsletter if the nl_cs query string or cookie variable is present.
	if (STSharedTracking.getUrlOrCookieVar('nl_cs')) {
		STSharedTracking.addData('marketingChannel', 'newsletter');
	}

	// Re-add numNights as int
	if ('undefined' !== typeof window.__stsharedTrackingObj.numNights) {
		STSharedTracking.addData('numNights', Number(window.__stsharedTrackingObj.numNights));
	}

	// set the Date onload based on cookie
	STSharedTracking.setDate();

	STSharedTracking.setEntryUrl();

	STSharedTracking.initPageStateId();

	// Skimlinks config (after tracking is set.)
	window.skimlinks_settings = window.skimlinks_settings || {};
	window.skimlinks_settings['skimlinks_tracking'] =
		(window.__stsharedTrackingObj && window.__stsharedTrackingObj.pageStateId) || '';
	window.skimlinks_settings['skimlinks_exclude'] = ['amazon.com', 'amazon.co.uk', 'amzn.com', 'amzn.to'];

	if (window.STSharedSkimlinksCode) {
		// add in the skimlinks scripts now that the tracking config is set.
		var s = document.createElement('script');
		s.type = 'text/javascript';
		s.src = `https://s.skimresources.com/js/${window.STSharedSkimlinksCode}.skimlinks.js`;
		document.body.appendChild(s);
	}

	if (window.dataLayer) {
		const trackingData = Object.assign({ event: 'dataAvailable' }, window.__stsharedTrackingObj);
		window.dataLayer.push(trackingData);
	}
};
STSharedTracking.init();
